/**
 * Resultado do motor fiscal (simulador, prévia e documento): pendências com o motivo exato,
 * cálculo por item e tributo, trilha de decisão (regra escolhida, descartadas e por quê).
 */
import {
  TAX_KIND_LABEL,
  TAX_LINE_LABEL,
  formatDecimalBR,
  formatMoney,
  type CalcIssue,
  type CalcResult,
  type TaxLineCode,
} from "@cafegestor/shared";
import { Fragment, useState } from "react";
import { Alert, Badge, Button, Table } from "@/components/ui";

export function IssueList({ issues }: { issues: CalcIssue[] }) {
  if (issues.length === 0) return null;
  return (
    <Alert tone="error">
      <p className="font-medium">
        Emissão bloqueada — {issues.length} pendência(s). Nenhuma tributação padrão é usada no
        lugar.
      </p>
      <ul className="mt-2 list-disc space-y-1 pl-5">
        {issues.map((i, n) => (
          <li key={n}>
            {i.itemLine ? <span className="font-medium">Item {i.itemLine}: </span> : null}
            {i.message}
            {i.nearest && i.nearest.length > 0 && (
              <span className="block text-xs">Regras próximas: {i.nearest.join("; ")}</span>
            )}
          </li>
        ))}
      </ul>
    </Alert>
  );
}

export function CalcView({ calc, compact = false }: { calc: CalcResult; compact?: boolean }) {
  const [open, setOpen] = useState<number | null>(null);
  const taxes = Object.entries(calc.totals.taxes) as [TaxLineCode, string][];
  return (
    <div className="space-y-4">
      <IssueList issues={calc.issues} />
      {calc.warnings.map((w) => (
        <Alert key={w} tone="warn">
          {w}
        </Alert>
      ))}
      <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
        <span>
          Modelo: <strong>{calc.model ?? "—"}</strong>{" "}
          <span className="text-muted-foreground">{calc.modelReason}</span>
        </span>
        <span>
          Operação: <strong>{calc.operation?.name ?? "—"}</strong>
          {calc.operation?.nature ? ` (${calc.operation.nature})` : ""}
        </span>
        <span>Destinatário: {calc.recipientLabel}</span>
        <span>Destino: {calc.destScope ?? "—"}</span>
      </div>
      <Table head={["#", "Produto", "NCM", "CFOP", "Qtd", "Total", "Tributos", ""]}>
        {calc.items.map((i) => (
          <Fragment key={i.line}>
            <tr>
              <td className="px-3 py-2">{i.line}</td>
              <td className="px-3 py-2">
                <span className="font-mono text-xs">{i.productCode}</span> {i.description}
              </td>
              <td className="px-3 py-2 font-mono">{i.ncm ?? "—"}</td>
              <td className="px-3 py-2 font-mono">{i.cfop ?? "—"}</td>
              <td className="px-3 py-2 text-right whitespace-nowrap">
                {i.quantity} {i.unitCode}
              </td>
              <td className="px-3 py-2 text-right">{formatMoney(i.total)}</td>
              <td className="px-3 py-2 text-xs">
                {i.taxes.map((t) => (
                  <span key={t.tax} className="mr-2 inline-block">
                    {TAX_LINE_LABEL[t.tax]} {formatMoney(t.amount)}
                  </span>
                ))}
              </td>
              <td className="px-3 py-2 text-right">
                {!compact && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setOpen(open === i.line ? null : i.line)}
                  >
                    {open === i.line ? "Fechar" : "Detalhes"}
                  </Button>
                )}
              </td>
            </tr>
            {open === i.line && (
              <tr>
                <td colSpan={8} className="bg-muted/40 px-3 py-3">
                  <ItemDetail item={i} />
                </td>
              </tr>
            )}
          </Fragment>
        ))}
      </Table>
      <div className="flex flex-wrap justify-end gap-x-6 gap-y-1 text-sm">
        <span>Produtos {formatMoney(calc.totals.products)}</span>
        {Number(calc.totals.discount) > 0 && (
          <span>Desconto {formatMoney(calc.totals.discount)}</span>
        )}
        {Number(calc.totals.freight) > 0 && <span>Frete {formatMoney(calc.totals.freight)}</span>}
        {taxes.map(([k, v]) => (
          <span key={k}>
            {TAX_LINE_LABEL[k]} {formatMoney(v)}
          </span>
        ))}
        <strong>Total do documento {formatMoney(calc.totals.document)}</strong>
      </div>
      {(calc.infoFisco || calc.infoContribuinte) && (
        <p className="text-xs text-muted-foreground">
          {calc.infoFisco} {calc.infoContribuinte}
        </p>
      )}
    </div>
  );
}

/** Percentual da API ("12.5000") no padrão brasileiro ("12,5%"). */
const pctBR = (v: string | number | null | undefined) =>
  v === null || v === undefined || v === "" ? "—" : `${formatDecimalBR(trimZeros(String(v)))}%`;
const trimZeros = (v: string) => (v.includes(".") ? v.replace(/0+$/, "").replace(/\.$/, "") : v);

function ItemDetail({ item }: { item: CalcResult["items"][number] }) {
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <div>
        <h4 className="mb-1 text-sm font-semibold">Cálculo por tributo</h4>
        <Table head={["Tributo", "CST", "Base orig.", "Red. %", "Base", "Alíq.", "Valor"]}>
          {item.taxes.map((t) => (
            <tr key={t.tax}>
              <td className="px-3 py-1">{TAX_LINE_LABEL[t.tax]}</td>
              <td className="px-3 py-1 font-mono">{t.cst ?? "—"}</td>
              <td className="text-right tabular-nums px-3 py-1">{formatMoney(t.baseOriginal)}</td>
              <td className="px-3 py-1">{pctBR(t.reductionPercent)}</td>
              <td className="text-right tabular-nums px-3 py-1">{formatMoney(t.base)}</td>
              <td className="text-right tabular-nums px-3 py-1">
                {t.rate !== null && t.rate !== undefined
                  ? pctBR(t.rate)
                  : t.ratePerUnit !== null && t.ratePerUnit !== undefined
                    ? `${formatMoney(t.ratePerUnit)}/un.`
                    : "—"}
              </td>
              <td className="text-right tabular-nums px-3 py-1 font-medium">
                {formatMoney(t.amount)}
              </td>
            </tr>
          ))}
        </Table>
      </div>
      <div className="space-y-2 text-sm">
        <h4 className="font-semibold">Regras escolhidas e descartadas</h4>
        {item.groups.map((g) => (
          <div key={g.kind} className="rounded border bg-card p-2">
            <div className="flex items-center gap-2">
              <strong>{TAX_KIND_LABEL[g.kind]}</strong>
              <Badge
                tone={
                  g.status === "aplicada"
                    ? "ok"
                    : g.status === "nao_aplicavel"
                      ? "neutral"
                      : "error"
                }
              >
                {g.status === "aplicada"
                  ? "aplicada"
                  : g.status === "ausente"
                    ? "sem regra"
                    : g.status === "ambigua"
                      ? "ambígua"
                      : "não se aplica"}
              </Badge>
              {g.required && <span className="text-xs text-muted-foreground">obrigatória</span>}
            </div>
            {g.selected && (
              <p className="mt-1 text-xs">
                <span className="font-mono">
                  {g.selected.ruleCode} v{g.selected.versionNo}
                </span>{" "}
                ({g.selected.status}) — {g.selected.reason} Condições:{" "}
                {g.selected.matched.join("; ")}
              </p>
            )}
            {g.ambiguous.length > 0 && (
              <p className="mt-1 text-xs text-destructive">Em conflito: {g.ambiguous.join(", ")}</p>
            )}
            {g.discarded.length > 0 && (
              <ul className="mt-1 list-disc pl-5 text-xs text-muted-foreground">
                {g.discarded.map((d) => (
                  <li key={d.versionId}>
                    {d.ruleCode} v{d.versionNo}: {d.reason}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * v7.1 — Tributos por item, à vista (prévia da Venda Direta): CST, base original, redução, base,
 * alíquota, valor e o benefício/classificação de cada tributo, com NCM e CFOP do item.
 */
export function TaxBreakdown({ calc }: { calc: CalcResult }) {
  return (
    <Table
      head={[
        "Item",
        "NCM",
        "CFOP",
        "Tributo",
        "CST",
        "Base orig.",
        "Red. %",
        "Base",
        "Alíq.",
        "Valor",
        "Benefício / classificação",
      ]}
    >
      {calc.items.flatMap((i) =>
        i.taxes.map((t, n) => {
          const extra = t.extra as Record<string, unknown>;
          const notes = [
            typeof extra["cBenef"] === "string" ? `cBenef ${extra["cBenef"]}` : null,
            typeof extra["cClassTrib"] === "string" ? `cClassTrib ${extra["cClassTrib"]}` : null,
            typeof extra["aliquotaCheia"] === "string"
              ? `alíquota cheia ${pctBR(extra["aliquotaCheia"])}`
              : null,
            typeof extra["cEnq"] === "string" ? `enquadramento ${extra["cEnq"]}` : null,
            typeof extra["modBC"] === "string" ? `modBC ${extra["modBC"]}` : null,
          ].filter((x): x is string => x !== null);
          return (
            <tr key={`${i.line}-${t.tax}`}>
              <td className="px-3 py-1">{n === 0 ? `${i.line} · ${i.productCode}` : ""}</td>
              <td className="px-3 py-1 font-mono">{n === 0 ? (i.ncm ?? "—") : ""}</td>
              <td className="px-3 py-1 font-mono">{n === 0 ? (i.cfop ?? "—") : ""}</td>
              <td className="px-3 py-1">{TAX_LINE_LABEL[t.tax]}</td>
              <td className="px-3 py-1 font-mono">{t.cst ?? "—"}</td>
              <td className="px-3 py-1 text-right">{formatMoney(t.baseOriginal)}</td>
              <td className="px-3 py-1 text-right">{pctBR(t.reductionPercent)}</td>
              <td className="px-3 py-1 text-right">{formatMoney(t.base)}</td>
              <td className="px-3 py-1 text-right">
                {t.rate !== null && t.rate !== undefined
                  ? pctBR(t.rate)
                  : t.ratePerUnit !== null && t.ratePerUnit !== undefined
                    ? `${formatMoney(t.ratePerUnit)}/un.`
                    : "—"}
              </td>
              <td className="px-3 py-1 text-right font-medium">{formatMoney(t.amount)}</td>
              <td className="px-3 py-1 text-xs">{notes.join(" · ") || "—"}</td>
            </tr>
          );
        }),
      )}
    </Table>
  );
}
